/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-08
 */

// Add third-party dependencies.
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Save,
  Cancel,
  CheckCircle,
  Error,
  Work,
  Home,
  Visibility,
  LocationOn,
  Email,
  Phone,
  Warning,
} from '@mui/icons-material';

// Add local dependencies.
import { useTranslation } from 'react-i18next';
import ProfileFormSkeleton from '../common/ProfileFormSkeleton';
import { useUpdateProfileMutation } from '../../features/auth';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useProfileFormData } from '../../hooks/useProfileData';
import { profileSchema } from '../../utils/validationSchemas';
import { validateAddressTranslated } from '../../utils/validationTranslated';
import { sanitizeErrorMessage } from '../../utils/security';
import {
  getCountryOptions,
  getStateOptions,
  getCountryByName,
  getStateLabel,
  countryHasStates,
} from '../../utils/countryData';

/**
 * Form data interface for profile editing.
 */
interface ProfileFormData {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  title: string;
  company: string;
  // Address fields
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  // Privacy settings
  addressVisible: boolean;
  emailVisible: boolean;
  phoneVisible: boolean;
  marketingEmails: boolean;
}

/**
 * Props for the ProfileEditForm component.
 */
interface ProfileEditFormProps {
  /**
   * Callback function called when the form is successfully saved. Receives
   * the new address when the email changed (a verification link was sent).
   */
  onSave?: (verificationSentTo?: string) => void;
  /**
   * Callback function called when the user cancels editing.
   */
  onCancel?: () => void;
}

/**
 * ProfileEditForm component that allows users to edit their profile information.
 * Includes form validation, error handling, and success feedback.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The profile edit form component
 *
 * @example
 * return (
 *   <ProfileEditForm
 *     onSave={() => setEditMode(false)}
 *     onCancel={() => setEditMode(false)}
 *   />
 * );
 */
const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  onSave,
  onCancel,
}) => {
  // Initialize translation hook for validation errors
  const { t } = useTranslation('errors');

  // Use the new profile data hook for data fetching and loading states
  const { initialData, isDataReady, user } = useProfileFormData();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const [formData, setFormData] = useState<ProfileFormData>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    title: '',
    company: '',
    // Address fields
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    // Privacy settings
    addressVisible: false,
    emailVisible: false,
    phoneVisible: false,
    marketingEmails: false,
  });

  const [apiError, setApiError] = useState<string>('');
  const [emailApiError, setEmailApiError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible';
    value: boolean;
  } | null>(null);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    email: 'idle' | 'valid' | 'invalid';
    firstName: 'idle' | 'valid' | 'invalid';
    lastName: 'idle' | 'valid' | 'invalid';
    phoneNumber: 'idle' | 'valid' | 'invalid';
  }>({
    email: 'idle',
    firstName: 'idle',
    lastName: 'idle',
    phoneNumber: 'idle',
  });

  // Initialize validation hook
  const { errors, validateForm, validateField } =
    useFormValidation<ProfileFormData>(profileSchema, {
      validateOnBlur: true,
    });

  // Initialize form data with current user information from the hook
  useEffect(() => {
    if (isDataReady && initialData && !formData.email) {
      // Only set form data if it hasn't been initialized yet
      setFormData(initialData);
    }
  }, [isDataReady, initialData, formData.email]);

  // Memoize hasChanges calculation to prevent unnecessary re-renders
  const hasChanges = useMemo(() => {
    if (!user || !initialData) return false;

    return (
      formData.email !== initialData.email ||
      formData.firstName !== initialData.firstName ||
      formData.lastName !== initialData.lastName ||
      formData.phoneNumber !== initialData.phoneNumber ||
      formData.title !== initialData.title ||
      formData.company !== initialData.company ||
      formData.streetAddress1 !== initialData.streetAddress1 ||
      formData.streetAddress2 !== initialData.streetAddress2 ||
      formData.city !== initialData.city ||
      formData.stateProvince !== initialData.stateProvince ||
      formData.postalCode !== initialData.postalCode ||
      formData.country !== initialData.country ||
      formData.addressVisible !== initialData.addressVisible ||
      formData.emailVisible !== initialData.emailVisible ||
      formData.phoneVisible !== initialData.phoneVisible ||
      formData.marketingEmails !== initialData.marketingEmails
    );
  }, [formData, initialData, user]);

  /**
   * Handles privacy setting changes with confirmation for making data public.
   */
  const handlePrivacySettingChange = (
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible',
    value: boolean
  ) => {
    // If making data public, show confirmation dialog
    if (value === true) {
      setPendingChange({ field, value });
      setConfirmDialogOpen(true);
    } else {
      // Making data private doesn't need confirmation
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear messages when user makes changes
    if (apiError) setApiError('');
    if (successMessage) setSuccessMessage('');
  };

  /**
   * Confirms the privacy setting change.
   */
  const confirmPrivacyChange = () => {
    if (pendingChange) {
      setFormData(prev => ({
        ...prev,
        [pendingChange.field]: pendingChange.value,
      }));
    }
    setConfirmDialogOpen(false);
    setPendingChange(null);
  };

  /**
   * Cancels the privacy setting change.
   */
  const cancelPrivacyChange = () => {
    setConfirmDialogOpen(false);
    setPendingChange(null);
  };

  /**
   * Gets the field label for display.
   */
  const getPrivacyFieldLabel = (
    field: 'addressVisible' | 'emailVisible' | 'phoneVisible'
  ): string => {
    switch (field) {
      case 'addressVisible':
        return 'address';
      case 'emailVisible':
        return 'email address';
      case 'phoneVisible':
        return 'phone number';
      default:
        return 'information';
    }
  };

  /**
   * Handles input changes for form fields.
   * @param e - The input change event
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      const newValue = type === 'checkbox' ? checked : value;

      setFormData(prev => {
        const updatedFormData = {
          ...prev,
          [name]: newValue,
        };

        // Validate field with updated data
        const error = validateField(
          name as keyof ProfileFormData,
          newValue,
          updatedFormData
        );

        // Update field validation states for visual feedback
        if (
          name === 'email' ||
          name === 'firstName' ||
          name === 'lastName' ||
          name === 'phoneNumber'
        ) {
          const fieldName = name as keyof typeof fieldValidationStates;
          const hasValue =
            typeof newValue === 'string' && newValue.trim().length > 0;

          let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
          if (error) {
            validationState = 'invalid';
          } else if (hasValue) {
            validationState = 'valid';
          }

          setFieldValidationStates(prevStates => ({
            ...prevStates,
            [fieldName]: validationState,
          }));
        }

        return updatedFormData;
      });

      // Clear messages when user starts typing
      if (apiError) setApiError('');
      if (successMessage) setSuccessMessage('');
      if (name === 'email') setEmailApiError('');
    },
    [validateField, apiError, successMessage]
  );

  /**
   * Handles country selection changes and resets state/province field.
   */
  const handleCountryChange = useCallback(
    (event: any) => {
      const countryName = event.target.value;

      setFormData(prev => ({
        ...prev,
        country: countryName,
        stateProvince: '', // Reset state/province when country changes
      }));

      // Clear messages when user makes changes
      if (apiError) setApiError('');
      if (successMessage) setSuccessMessage('');
    },
    [apiError, successMessage]
  );

  /**
   * Handles state/province selection changes.
   */
  const handleStateChange = useCallback(
    (event: any) => {
      const stateName = event.target.value;

      setFormData(prev => ({
        ...prev,
        stateProvince: stateName,
      }));

      // Clear messages when user makes changes
      if (apiError) setApiError('');
      if (successMessage) setSuccessMessage('');
    },
    [apiError, successMessage]
  );

  /**
   * Handles form submission.
   * @param e - The form submit event
   */
  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: keyof typeof fieldValidationStates) => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setEmailApiError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    // Validate form fields
    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    // Validate address fields with all-or-nothing logic
    const addressErrors = validateAddressTranslated(formData, t);
    if (Object.keys(addressErrors).length > 0) {
      // Set address validation errors
      Object.entries(addressErrors).forEach(([field]) => {
        setFieldValidationStates(prev => ({
          ...prev,
          [field]: 'invalid',
        }));
      });
      setApiError(
        'Please complete all required address fields or leave the address section empty.'
      );
      setIsSubmitting(false);
      return;
    }

    try {
      // Build address object if any address fields are filled
      const hasAddressData =
        formData.streetAddress1 ||
        formData.city ||
        formData.stateProvince ||
        formData.country;

      const addressData = hasAddressData
        ? {
            street_address_1: formData.streetAddress1,
            street_address_2: formData.streetAddress2 || undefined,
            city: formData.city,
            state_province: formData.stateProvince,
            postal_code: formData.postalCode || undefined,
            country: formData.country,
          }
        : null;

      // The backend lowercases the email and sends a verification link to a
      // changed address (todo/41).
      const newEmail = formData.email.trim().toLowerCase();
      const emailChanged =
        !!initialData && newEmail !== initialData.email.toLowerCase();

      const result = await updateProfile({
        // Basic user fields
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber || undefined,
        marketing_emails: formData.marketingEmails,
        // Profile fields
        title: formData.title || undefined,
        company: formData.company || undefined,
        // Address and privacy fields
        address: addressData,
        address_visible: formData.addressVisible,
        email_visible: formData.emailVisible,
        phone_visible: formData.phoneVisible,
      }).unwrap();

      setSuccessMessage(result.message || 'Profile updated successfully!');

      // Call onSave callback after a brief delay to show success message
      if (onSave) {
        setTimeout(() => {
          onSave(emailChanged ? newEmail : undefined);
        }, 1500);
      }
    } catch (error: any) {
      // Email refusals (address in use, too many changes today) come back
      // as a 400 under `email`: show them on the field. Other field errors
      // in the same 400 still get the generic alert.
      const emailErrors = error?.status === 400 ? error.data?.email : null;
      if (Array.isArray(emailErrors) && typeof emailErrors[0] === 'string') {
        setEmailApiError(emailErrors[0]);
        setFieldValidationStates(prev => ({ ...prev, email: 'invalid' }));
        if (Object.keys(error.data).some(key => key !== 'email')) {
          setApiError(sanitizeErrorMessage(error));
        }
      } else {
        setApiError(sanitizeErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles form cancellation.
   */
  const handleCancel = useCallback(() => {
    // Reset form to original data from the hook
    if (initialData) {
      setFormData(initialData);
    }
    setApiError('');
    setEmailApiError('');
    setSuccessMessage('');

    if (onCancel) {
      onCancel();
    }
  }, [initialData, onCancel]);

  // Show skeleton while data is loading
  if (!isDataReady || !user) {
    return <ProfileFormSkeleton fieldCount={12} />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Edit Profile
      </Typography>

      <Fade in={!!apiError}>
        <Box>
          {apiError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {apiError}
            </Alert>
          )}
        </Box>
      </Fade>

      <Fade in={!!successMessage}>
        <Box>
          {successMessage && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {successMessage}
            </Alert>
          )}
        </Box>
      </Fade>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Name Fields */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={isUpdating || isSubmitting}
            required
            InputProps={{
              endAdornment: getValidationIcon('firstName'),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={isUpdating || isSubmitting}
            required
            InputProps={{
              endAdornment: getValidationIcon('lastName'),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        </Box>

        {/* Email Field */}
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email || !!emailApiError}
          helperText={errors.email || emailApiError}
          disabled={isUpdating || isSubmitting}
          required
          InputProps={{
            endAdornment: getValidationIcon('email'),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {/* Phone Number Field */}
        <TextField
          fullWidth
          label="Phone Number (Optional)"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          disabled={isUpdating || isSubmitting}
          InputProps={{
            endAdornment: getValidationIcon('phoneNumber'),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {/* Marketing Emails Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              name="marketingEmails"
              checked={formData.marketingEmails}
              onChange={handleInputChange}
              disabled={isUpdating}
            />
          }
          label={
            <Typography variant="body2">
              I would like to receive marketing emails and updates
            </Typography>
          }
        />

        <Divider sx={{ my: 3 }} />

        {/* Professional Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Work fontSize="small" />
            Professional Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your professional title and company information
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Title (Optional)"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              error={!!errors.title}
              helperText={errors.title}
              disabled={isUpdating || isSubmitting}
              placeholder="e.g., Dance Instructor, Studio Owner"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
            <TextField
              fullWidth
              label="Company (Optional)"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              error={!!errors.company}
              helperText={errors.company}
              disabled={isUpdating || isSubmitting}
              placeholder="e.g., Your Dance Studio Name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Address Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Home fontSize="small" />
            Address Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your address information (optional)
          </Typography>

          {/* Address completion guidance */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Address Guidelines:</strong> Address information is
              completely optional. However, if you choose to provide an address,
              please complete at least:
              <strong> Street Address, City, and Country</strong> for a valid
              address.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Street Address"
            name="streetAddress1"
            value={formData.streetAddress1}
            onChange={handleInputChange}
            error={!!errors.streetAddress1}
            helperText={errors.streetAddress1}
            disabled={isUpdating || isSubmitting}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />

          <TextField
            fullWidth
            label="Street Address 2 (Optional)"
            name="streetAddress2"
            value={formData.streetAddress2}
            onChange={handleInputChange}
            error={!!errors.streetAddress2}
            helperText={errors.streetAddress2}
            disabled={isUpdating || isSubmitting}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />

          {/* Country Selection */}
          <FormControl
            fullWidth
            error={!!errors.country}
            disabled={isUpdating || isSubmitting}
            sx={{ mb: 2 }}
          >
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              labelId="country-select-label"
              id="country-select"
              value={formData.country}
              label="Country"
              onChange={handleCountryChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            >
              <MenuItem value="">
                <em>Select a country</em>
              </MenuItem>
              {getCountryOptions().map(country => (
                <MenuItem key={country.code} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            {errors.country && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                {errors.country}
              </Typography>
            )}
          </FormControl>

          {/* State/Province/Region Selection */}
          {formData.country &&
            (countryHasStates(
              getCountryByName(formData.country)?.code || ''
            ) ? (
              // Show dropdown for countries with states (US, CA, AU, GB)
              <FormControl
                fullWidth
                error={!!errors.stateProvince}
                disabled={isUpdating || isSubmitting}
                sx={{ mb: 2 }}
              >
                <InputLabel id="state-select-label">
                  {getStateLabel(
                    getCountryByName(formData.country)?.code || ''
                  )}
                </InputLabel>
                <Select
                  labelId="state-select-label"
                  id="state-select"
                  value={formData.stateProvince}
                  label={getStateLabel(
                    getCountryByName(formData.country)?.code || ''
                  )}
                  onChange={handleStateChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                      '&.Mui-focused fieldset': {
                        borderWidth: '2px',
                      },
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>
                      Select{' '}
                      {getStateLabel(
                        getCountryByName(formData.country)?.code || ''
                      )}
                    </em>
                  </MenuItem>
                  {getStateOptions(
                    getCountryByName(formData.country)?.code || ''
                  ).map(state => (
                    <MenuItem key={state.code} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.stateProvince && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, ml: 2 }}
                  >
                    {errors.stateProvince}
                  </Typography>
                )}
              </FormControl>
            ) : (
              // Show text field for countries without formal states
              <TextField
                fullWidth
                label={getStateLabel(
                  getCountryByName(formData.country)?.code || ''
                )}
                name="stateProvince"
                value={formData.stateProvince}
                onChange={handleInputChange}
                error={!!errors.stateProvince}
                helperText={errors.stateProvince}
                disabled={isUpdating || isSubmitting}
                placeholder="Enter region (optional)"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '2px',
                    },
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              />
            ))}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              error={!!errors.city}
              helperText={errors.city}
              disabled={isUpdating || isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
            <TextField
              fullWidth
              label="Postal Code (Optional)"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              disabled={isUpdating || isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Privacy Settings Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Visibility fontSize="small" />
            Privacy Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Control what information is visible to other members of your
            studios. All information is private by default.
          </Typography>

          <Box sx={{ mb: 3 }}>
            {/* Address Visibility */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.addressVisible}
                    onChange={e =>
                      handlePrivacySettingChange(
                        'addressVisible',
                        e.target.checked
                      )
                    }
                    disabled={isUpdating || isSubmitting}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOn sx={{ mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body1">
                        Show my address to studio members
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Other members will be able to see your full address
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Email Visibility */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emailVisible}
                    onChange={e =>
                      handlePrivacySettingChange(
                        'emailVisible',
                        e.target.checked
                      )
                    }
                    disabled={isUpdating || isSubmitting}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Email sx={{ mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body1">
                        Show my email to studio members
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Other members will be able to see your email address
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Phone Visibility */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.phoneVisible}
                    onChange={e =>
                      handlePrivacySettingChange(
                        'phoneVisible',
                        e.target.checked
                      )
                    }
                    disabled={isUpdating || isSubmitting}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Phone sx={{ mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body1">
                        Show my phone number to studio members
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Other members will be able to see your phone number
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Privacy Notice:</strong> Your information will only be
              visible to other members of studios you belong to. Studio owners
              and administrators may have access to additional information as
              needed for studio management.
            </Typography>
          </Alert>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            disabled={isUpdating || isSubmitting}
            startIcon={<Cancel />}
            sx={{
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 1,
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isUpdating || isSubmitting || !hasChanges}
            startIcon={
              isUpdating || isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                <Save />
              )
            }
            sx={{
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 2,
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {isUpdating || isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={cancelPrivacyChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1, color: 'warning.main' }} />
          Confirm Privacy Change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to make your{' '}
            {pendingChange
              ? getPrivacyFieldLabel(pendingChange.field)
              : 'information'}{' '}
            visible to other studio members?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This means other members of your studios will be able to see this
            information. You can change this setting back to private at any
            time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPrivacyChange}>Cancel</Button>
          <Button onClick={confirmPrivacyChange} variant="contained">
            Make Visible
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProfileEditForm;
